import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLayoutEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';

export interface UseSortableOptionArgs {
  id: string;
  index: number;
  count: number;
  axis: 'x' | 'y';
  onMove: (from: number, to: number) => void;
}

/** One row or chip of a `SortableOptionList`; spread `handle` on what the person grabs. */
export function useSortableOption({ id, index, count, axis, onMove }: UseSortableOptionArgs) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const element = useRef<HTMLElement | null>(null);
  const keepFocus = useRef(false);

  // React moves DOM nodes to reorder them, and moving a focused node drops its focus.
  useLayoutEffect(() => {
    if (!keepFocus.current) return;
    keepFocus.current = false;
    element.current?.focus();
  });

  const onKeyDown = (event: KeyboardEvent) => {
    const back = axis === 'y' ? 'ArrowUp' : 'ArrowLeft';
    const ahead = axis === 'y' ? 'ArrowDown' : 'ArrowRight';
    if (event.key !== back && event.key !== ahead) return;
    event.preventDefault();
    event.stopPropagation();
    const to = index + (event.key === ahead ? 1 : -1);
    if (to < 0 || to >= count) return;
    keepFocus.current = true;
    onMove(index, to);
  };

  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
  return {
    setNodeRef,
    style,
    isDragging,
    handle: {
      ...attributes,
      ...listeners,
      ref: (node: HTMLElement | null) => {
        element.current = node;
        setActivatorNodeRef(node);
      },
      onKeyDown,
    },
  };
}
